import { OtsDateTime } from "../components/OtsDateTime";
import { OtsFlaggedValue } from "../components/OtsFlaggedValue";
import { OtsInlineEdit } from "../components/OtsInlineEdit";
import { OtsTooltip } from "../components/OtsTooltip";
import { ActivityDayOffset } from "./ActivityDayOffset";
import { ActivityGeofenceChip } from "./ActivityGeofenceChip";
import { ActivityMarkerIcons } from "./ActivityMarkerIcons";
import { formatActivityQuantity, formatActivityTime } from "./driver-activity-format";
import { activityCellClass, activityGroupStartClass, activityMutedClass } from "./driver-activity-layout";
import type { ActivityCellFlag, ActivityGeofenceMatch, ActivityMarker, ActivityStop } from "./driver-activity-view-model";

export type ActivityStopCellsProps = {
  side: "pickup" | "delivery";
  /** Missing when a ticket has only the other side; the three cells collapse into a notice. */
  transport?: ActivityStop;
  time?: number;
  timeZone?: string;
  /** The day being viewed; times on other days get a +1d / −1d badge. */
  day?: Date;
  ticketFlag?: ActivityCellFlag;
  timeFlag?: ActivityCellFlag;
  siteFlag?: ActivityCellFlag;
  qtyFlag?: ActivityCellFlag;
  /** Split load, restriction and the like, shown before the site name. */
  markers?: ActivityMarker[];
  /** The geofence visit matched to the stop, shown beside the ticket time. */
  geofence?: ActivityGeofenceMatch;
  /** Makes the ticket number and quantity editable in place. A validated, locked quantity stays read-only. */
  onEdit?: (change: { ticketNumber?: string; quantity?: number }) => void | Promise<unknown>;
};

/** The time · geofence · ticket number, site, and quantity cells for one side of a load. */
export const ActivityStopCells = ({ side, transport, time, timeZone, day, ticketFlag, timeFlag, siteFlag, qtyFlag, markers = [], geofence, onEdit }: ActivityStopCellsProps) => {
  if (!transport) {
    return (
      <div role="gridcell" className={[activityCellClass, activityGroupStartClass, "col-span-3"].join(" ")}>
        <span className="w-fit rounded border border-dashed border-[var(--ots-warning-outline)] px-2 py-0.5 text-xs font-medium text-[var(--ots-on-warning-container)]">
          No {side} matched
        </span>
      </div>
    );
  }

  const locked = markers.some((marker) => marker.id === "qty-locked");
  const ticketNumberLine = (
    <OtsFlaggedValue {...ticketFlag} className={["text-xs", ticketFlag ? "" : activityMutedClass].join(" ")}>
      {transport.ticketNumber ? `#${transport.ticketNumber}` : "No ticket #"}
    </OtsFlaggedValue>
  );
  const quantity = <OtsFlaggedValue {...qtyFlag}>{formatActivityQuantity(transport.quantityDecimal)}</OtsFlaggedValue>;

  return <>
    <div role="gridcell" className={[activityCellClass, activityGroupStartClass].join(" ")}>
      {/* When it happened and whether the truck was really there lead; the paper ticket number is the footnote. */}
      <span className="flex min-w-0 items-center gap-1.5">
        <OtsFlaggedValue {...timeFlag} className="shrink-0 font-semibold tabular-nums">
          <OtsDateTime value={time} format="time" timeZone={timeZone}/>
        </OtsFlaggedValue>
        <ActivityDayOffset time={time} day={day} timeZone={timeZone}/>
        <ActivityGeofenceChip match={geofence} ticketTime={time} timeZone={timeZone}/>
      </span>
      {onEdit
        ? <OtsInlineEdit
          label={`${side} ticket number`}
          value={transport.ticketNumber ?? ""}
          onSave={(ticketNumber) => onEdit({ ticketNumber })}
          className="text-xs">
          {ticketNumberLine}
        </OtsInlineEdit>
        : ticketNumberLine}
    </div>
    <div role="gridcell" className={activityCellClass}>
      <span className="flex min-w-0 items-center gap-1">
        <ActivityMarkerIcons markers={markers.filter((marker) => marker.id !== "qty-locked")}/>
        <OtsFlaggedValue {...siteFlag} className="font-medium">{transport.siteName ?? "Unknown site"}</OtsFlaggedValue>
      </span>
      {/* The tank's short name where it has one; the full name is on hover or focus. */}
      <OtsTooltip density="compact" className="min-w-0 max-w-full" content={transport.containerAbbr && transport.containerName ? transport.containerName : undefined}>
        <span
          tabIndex={transport.containerAbbr && transport.containerName ? 0 : undefined}
          className={["truncate text-xs outline-none focus-visible:ring-2 focus-visible:ring-[var(--ots-primary)]", activityMutedClass].join(" ")}>
          {[transport.containerAbbr || transport.containerName, transport.wellName && `${transport.wellName}${transport.stage ? ` (${[transport.stage, transport.subStage].filter(Boolean).join(" / ")})` : ""}`, transport.siteCompanyName].filter(Boolean).join(" · ")}
        </span>
      </OtsTooltip>
    </div>
    <div role="gridcell" className={[activityCellClass, "items-end tabular-nums"].join(" ")}>
      <span className="flex max-w-full items-center gap-1">
        <ActivityMarkerIcons markers={markers.filter((marker) => marker.id === "qty-locked")}/>
        {onEdit && !locked
          ? <OtsInlineEdit
            label={`${side} quantity`}
            type="number"
            align="end"
            value={String(transport.quantityDecimal)}
            validate={(value) => value === "" || !Number.isFinite(Number(value)) || Number(value) < 0 ? "Enter a quantity of zero or more" : undefined}
            onSave={(quantity) => onEdit({ quantity: Number(quantity) })}>
            {quantity}
          </OtsInlineEdit>
          : quantity}
      </span>
      <span className={["text-xs", activityMutedClass].join(" ")}>{transport.quantityUnitOfMeasure}</span>
    </div>
  </>;
};
